import { BaseListViewStore, BooleanCellData, HyperlinkCellData, ListCellData, ShippingCostCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import ShipmentInfoStore from './ShipmentInfoStore';
import { BaasicTableSettingsViewStore } from 'common/stores';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	isWarehouse,
	isSuperAdminOrganization,
	isSeller,
} from 'core/utils';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { channelType, dateFilterEnum, lookupRouteName, shipmentInfoTypeEnum } from 'common/constants';
import { DateTimeCellData } from 'core/stores';
import {
	FulfillmentOrderService,
	PalletService,
	CarrierServiceService,
	ChannelService,
	ApplicationService,
	OrderService,
	ProductService,
	CarrierService,
} from 'common/data';
import {
	OrderChannelHyperLinkCellData,
	HyperLinkTrackingNumberCellData,
	TooltipDateTimeCellData,
} from 'core/stores/CellDataStore';
import _ from 'lodash';
import { OrderDateRangeViewStore } from 'modules/order/stores';

class ShipmentInfoListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable showYesterdayShipments;
	localStorageWidthKey = 'shipment-infos-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ShipmentInfoStore(rootStore),
			queryParams: {
				embed: [
					'warehouse',
					'warehouse.timezone',
					'fulfillmentOrder',
					'pallet',
					// required for the backend inventory deduction during the find call in case the shipment has been picked up
					'fulfillmentOrder.fulfillmentOrderItems',
					'fulfillmentOrder.fulfillmentOrderStatus',
					'fulfillmentOrder.order',
					'fulfillmentOrder.order.channel',
					'fulfillmentOrder.order.channel.channelType',
					'seller',
					'seller.sellerOrganizationInfo',
					'carrier',
					'carrier.carrierType',
					'carrierDeliveryStatus',
					'carrierDeliveryStatus.shipmentInfoDeliveryStatus',
					'carrierService',
					'carrierService.carrierType',
					'shipmentInfoTrackingEvents',
				],
				filter: localStorageHandler.get('shipment-infos-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'trackingNumber',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGNUMBER',
						dataCell: HyperLinkTrackingNumberCellData,
						carrierTypePath: 'fulfillmentOrder.carrierService.carrierType.name',
						selected: true,
						service: 'shipmentInfoService',
						width: 200,
						filter: null,
						locked: true,
						sortable: true,
						filterType: TableFilterTemplate,
						service: 'shipmentInfoService',
						filterApiParams: {
							shippedOnly: true,
							ignoreEmptyTracking: true,
							shipmentInfoType: shipmentInfoTypeEnum.shipment,
						},
					},
					{
						key: 'seller.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SELLER',
						service: 'applicationService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'applicationTitle',
						enabled: !isSeller(rootStore),
						dontSplit: true,
						filterApiParams: {
							sellerOnly: true,
						},
					},
					{
						key: 'warehouse.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SHIPFROMWAREHOUSE',
						service: 'applicationService',
						isClickable: false,
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
						embed: ['warehouse'],
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'fulfillmentOrder.order.channel.name',
						title: 'SHIPMENTSTATUSVIEW.CHANNEL',
						service: 'channelService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						canFilter: true,
						dontSplit: true,
						embed: ['fulfillmentOrder', 'fulfillmentOrder.order', 'fulfillmentOrder.order.channel'],
						searchValues: 'name',
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
						},
					},
					{
						key: 'fulfillmentOrder.order.channel.channelType.name',
						title: 'SHIPMENTSTATUSVIEW.CHANNELTYPE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						lookupConstant: lookupRouteName.channelType,
					},
					{
						key: 'fulfillmentOrder.order.orderNumber',
						title: 'SHIPMENTSTATUSVIEW.ORDERNUMBER',
						service: 'orderService',
						selected: false,
						dataCell: OrderChannelHyperLinkCellData,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						canFilter: true,
						dontSplit: true,
						embed: ['fulfillmentOrder', 'fulfillmentOrder.order'],
						searchValues: 'orderNumber',
						filterApiParams: {
							shipmentInfoType: shipmentInfoTypeEnum.shipment,
							isWarehouse: isWarehouse(rootStore),
							checkPartiallyFulfillMixedOrders: true,
							shippedOnly: true,
						},
						linkParamField: 'fulfillmentOrder.orderId',
						url: (order, channelTypeName) => this.getOrderChannelUrl(order, channelTypeName)
					},
					{
						key: 'fulfillmentOrder.order.dateCreated',
						title: 'SHIPMENTSTATUSVIEW.ORDERCREATIONDATE',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
						advancedFilterFieldName: 'FulfillmentOrder.Order.DateCreated',
						timezonePath: 'warehouse.timezone.name'
					},
					{
						key: 'fulfillmentOrder.fulfillmentOrderId',
						title: 'SHIPMENTSTATUSVIEW.FULFILLMENTORDERID',
						service: 'fulfillmentOrderService',
						dataCell: HyperlinkCellData,
						routeName: 'master.app.main.order.list.detail',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
						dontSplit: true,
					},
					{
						key: 'estimatedArrivalDate',
						title: 'SHIPMENTSTATUSVIEW.ESTIMATEDDELIVERYDATE',
						dateFormat: 'L',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterDateTemplate,
						dontConvertTime: true
					},
					{
						key: 'warehouseShippingDate',
						title: 'SHIPMENTSTATUSVIEW.SHIPPINGDATE',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						dontConvertTime: true,
						useLocalTime: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						timezonePath: 'warehouse.timezone.name'
					},
					{
						key: 'cost',
						title: 'SHIPMENTSTATUSVIEW.COST',
						dataCell: ShippingCostCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'isBuyShipping',
						title: 'SHIPMENTSTATUSVIEW.ISBUYSHIPPING',
						dataCell: BooleanCellData,
						selected: false,
						sortable: true,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
					},
					{
						key: 'fulfillmentOrder.order.isPrime',
						title: 'SHIPMENTSTATUSVIEW.ISPRIME',
						dataCell: BooleanCellData,
						selected: false,
						sortable: true,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
					},
					{
						key: 'carrierService.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERSERVICE',
						service: 'carrierServiceService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
						title: 'SHIPMENTSTATUSVIEW.FULFILLMENTORDERSTATUS',
						lookupConstant: lookupRouteName.fulfillmentOrderStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'carrierDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERDELIVERYSTATUS',
						lookupConstant: lookupRouteName.carrierDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus'],
					},
					{
						key: 'carrierOriginScan',
						title: 'SHIPMENTSTATUSVIEW.CARRIERORIGINSCAN',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'carrierDeliveryStatus.shipmentInfoDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.SHIPMENTINFODELIVERYSTATUS',
						lookupConstant: lookupRouteName.shipmentInfoDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterTemplate,
						dontSplit: true,
						embed: ['carrierDeliveryStatus', 'carrierDeliveryStatus.shipmentInfoDeliveryStatus'],
					},
					{
						key: 'zone',
						title: 'SHIPMENTSTATUSVIEW.ZONE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
					},
					{
						key: 'fulfillmentOrder.deliverByDate',
						title: 'SHIPMENTSTATUSVIEW.DELIVERBYDATE',
						dateFormat: 'L',
						dataCell: TooltipDateTimeCellData,
						newPropertyToUse: 'fulfillmentOrder.newDeliverByDate',
						translationKey: 'SHIPMENTSTATUSVIEW.NEWDELIVERBYDATE',
						service: 'fulfillmentOrderService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						useLocalTime: false,
					},
					{
						key: 'fulfillmentOrder.shipByDate',
						title: 'SHIPMENTSTATUSVIEW.SHIPBYDATE',
						dateFormat: 'L',
						dataCell: TooltipDateTimeCellData,
						service: 'fulfillmentOrderService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						useLocalTime: false,
					},
					{
						key: 'fulfillmentOrder.whShipByDate',
						title: 'SHIPMENTSTATUSVIEW.WHSHIPBYDATE',
						dataCell: TooltipDateTimeCellData,
						dateFormat: 'L',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
					},
					{
						key: 'deliveryDate',
						title: 'SHIPMENTSTATUSVIEW.DELIVERYDATE',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterDateTemplate,
						dontConvertTime: true
					},
					{
						key: 'carrierOriginScanDate',
						title: 'SHIPMENTSTATUSVIEW.CARRIERORIGINSCANDATE',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterDateTemplate,
						dontConvertTime: true
					},
					{
						key: 'fulfillmentOrder.fulfillmentOrderItems.sku',
						title: 'SHIPMENTSTATUSVIEW.PRODUCTSKU',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						dontSplit: true,
						service: 'productService',
						filterKey: 'mainSku',
						filterType: TableFilterTemplate,
						dataCell: ListCellData,
					},
					{
						key: 'pallet.name',
						title: 'SHIPMENTSTATUSVIEW.PALLET',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterTemplate,
						service: 'palletService',
						dontSplit: true,
					},
					{
						key: 'trackingEvent1',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENT1',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'trackingEventDate1',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENTDATE1',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
					},
					{
						key: 'trackingEvent2',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENT2',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'trackingEventDate2',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENTDATE2',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
					},
					{
						key: 'trackingEvent3',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENT3',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'trackingEventDate3',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENTDATE3',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
					},
					{
						key: 'trackingEvent4',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENT4',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'trackingEventDate4',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENTDATE4',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
					},
					{
						key: 'trackingEvent5',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENT5',
						selected: false,
						width: 200,
						filter: null,
					},
					{
						key: 'trackingEventDate5',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGEVENTDATE5',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
					},
					{
						key: 'promisedDeliveryDate',
						title: 'SHIPMENTSTATUSVIEW.PROMISEDDELIVERYDATE',
						dateFormat: 'L',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						dontConvertTime: true,
						sortable: true,
						filterType: TableFilterDateTemplate,
					},
					{
						key: 'daysInTransit',
						title: 'SHIPMENTSTATUSVIEW.DAYSINTRANSIT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'carrier.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIER',
						service: 'carrierService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true
					},
					{
						key: 'carrier.carrierType.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERTYPE',
						lookupConstant: lookupRouteName.carrierType,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterTemplate,
					},
				],
				sortObject: [
					{
						field: 'trackingNumber',
						dir: 'asc',
					},
				],
				actions: {},
			},
			permissions: {
				update: () => this.authStore.hasPermission('fulfillmentSection.update'),
				create: () => this.authStore.hasPermission('fulfillmentSection.create'),
				remove: () => this.authStore.hasPermission('fulfillmentSection.remove'),
				read: () => this.authStore.hasPermission('fulfillmentSection.read'),
				full: () => this.authStore.hasPermission('fulfillmentSection.full'),
			},
			routes: {},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (!isSeller(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		checkForColumnFilter(localStorageHandler.get('shipment-infos-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(this.rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new FulfillmentOrderService(this.rootStore.app.baasic.apiClient),
				new CarrierServiceService(this.rootStore.app.baasic.apiClient),
				new ChannelService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
				new OrderService(this.rootStore.app.baasic.apiClient),
				new ProductService(this.rootStore.app.baasic.apiClient),
				new PalletService(this.rootStore.app.baasic.apiClient),
				new CarrierService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setShowYesterdayShipments();
		this.setSearchVisible();
		this.setButtons();
		this.setHeaderModes();
		this.orderDateRangeViewStore = new OrderDateRangeViewStore({
			queryUtility: this.queryUtility,
			findFunction: this.findShipmentInfos,
			localStorageHandlerKey: 'shipments-date-filter-option',
			initialValue: dateFilterEnum[30]
		});
		await this.queryUtility.initialize(this.findShipmentInfos);
		this.resume();
	}

	@action.bound
	setShowYesterdayShipments() {
		var showYesterdayShipments = localStorageHandler.get('show-yesterday-shipments');
		if (showYesterdayShipments != null) {
			this.showYesterdayShipments = showYesterdayShipments;
		} else {
			this.showYesterdayShipments = true;
		}
	}
	openOrderPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};
	getOrderChannelUrl = (order, channelTypeName) => {
		switch (channelTypeName) {
			case channelType.amazon:
				return`https://sellercentral.amazon.com/orders-v3/order/${order.orderNumber}`;
			case channelType.wooCommerce:
				return `https://milliardbedding.com/wp-admin/post.php?post=${order.orderNumber}&action=edit`;
			case channelType.wayfair:
				return `https://partners.wayfair.com/d/outbound-orders/po/${order.orderNumber}`;
			default:
				return `https://portal.logicbroker.com/order-management/order-details?orderid=${order.logicBrokerKey}`;
		}
	}

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		});
	}

	@action.bound async findShipmentInfos() {
		this.suspend();
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ShipmentInfosTableSettings',
			this.setAsChanged,
			this.findShipmentInfos
		);
		this.queryUtility.setFilterParams(
			this,
			{
				ignoreEmptyTracking: true,
				shippedOnly: true,
				showYesterdayShipments: this.showYesterdayShipments,
				shipmentInfoType: shipmentInfoTypeEnum.shipment,
			},
			this.tableFilter
		);
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'shipment-infos-filter', 'shipment-infos-table-data');
		this.setButtons();
		this.resume();
	}

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}

	@action.bound onCheck = async check => {
		if (check) {
			this.showYesterdayShipments = check.currentTarget.checked;
			localStorageHandler.set('show-yesterday-shipments', this.showYesterdayShipments);
			this.orderDateRangeViewStore.todayFilterChange(check.currentTarget.checked);
			if (this.showYesterdayShipments) {
				this.findShipmentInfos();
			}
		}
	};
}

export default ShipmentInfoListViewStore;

